<template>
  <AuthenticatedLayout activeMenuItem='report-historical-asset' title='Reports'>
    <LoadingLayout :isLoading='!pageReady'>
      <PaddedLayout>
        <div class='flex flex-wrap flex-col sm:flex-row py-2 justify-between sm:items-center gap-2 w-full max-w-[80rem]'>
          <h1 class='text-2xl'>Historical Asset Report</h1>
          <a :href='pdfUrl' target='_blank' type="submit" class="rounded-md bg-jananavy-900 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-jananavy-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-jananavy-600">Export PDF</a>
        </div>
        <div class='flex gap-2 items-center mt-2 max-w-[80rem]'>
          <SingleRelationInput collection='organisation-asset' :srLabel='true' label='Asset' v-model='state.filters.organisationAsset' placeholder='Search Asset...' v-if='isOrganisationView()' class='w-full' />
          <SingleRelationInput collection='platform-asset' :srLabel='true' label='Asset' v-model='state.filters.platformAsset' placeholder='Search Asset...' v-if='isSuperAdminView()' class='w-full' />
          <TabbedNumberInput v-model='state.filters.year.from' label='Year From' :hideLabel='true' class='w-full' />
          <TabbedNumberInput v-model='state.filters.year.to' label='Year To' :hideLabel='true' class='w-full' />
        </div>
        <div class='mt-2'>
          <TabView :tabs='tabs' v-model='state.tab' />

          <div v-if='state.filters.organisationAsset || state.filters.platformAsset'>
            <div v-if='state.tab === "results"' class='mt-8'>
              <GraphHistoricalPortfolioTable
                :params='params'
                :showOrganisation='isSuperAdminView()'
                :showAssetManager='true'
                :view='view'
                :showFilter='true'
              >
                <template #title>Results</template>
                <template #description>Scores for all engagements.</template>
              </GraphHistoricalPortfolioTable>
            </div>

            <div v-if='state.tab === "themes"' class='mt-8 max-w-[80rem]'>
              <GraphHistoricalThemeBar :params='params' :showKey='true'>
                <template #title>Theme Trends</template>
                <template #description>Theme transformation year on year.</template>
              </GraphHistoricalThemeBar>
            </div>
          </div>

          <div v-else class="relative block w-full rounded-lg border-2 border-dashed border-gray-300 p-12 text-center max-w-[80rem] mt-8">
            <span class="mt-2 block text-sm font-medium text-gray-900">Select an asset to generate a report</span>
          </div>

        </div>
      </PaddedLayout>
    </LoadingLayout>
  </AuthenticatedLayout>
</template>

<script setup>

import { computed, reactive, watch, ref, onActivated } from 'vue';

import { debounce } from '@/kernel/debounce';
import { appendStateToRoute, getStateFromRoute } from '@/router';
import { dehydrateCollections, rehydrateCollections, getReportPDFExportUrl } from '@/kernel/report';

import AuthenticatedLayout from '@/components/layouts/authenticated-layout.vue';
import PaddedLayout from '@/components/layouts/padded-layout.vue';
import LoadingLayout from '@/components/layouts/loading-layout.vue';

import TabView from '@/components/views/tab-view.vue';

import TabbedNumberInput from '@/components/inputs/tabbed-number-input.vue';
import SingleRelationInput from '@/components/inputs/single-relation-input.vue';

import GraphHistoricalPortfolioTable from '@/components/reports/graphs/graph-historical-portfolio-table.vue';
import GraphHistoricalThemeBar from '@/components/reports/graphs/graph-historical-theme-bar.vue';

import { getOrganisationId, isSuperAdminView, isOrganisationView } from '@/kernel/session';

const tabs = [
  { name: 'Results', key: 'results' },
  { name: 'Themes', key: 'themes' }
];

const view = computed(() => {
  return isSuperAdminView() ? 'platform' : 'organisation';
});

const organisationId = computed(() => {
  return getOrganisationId();
});

const filterCollectionMap = [
  {key: 'organisationAsset', collection: 'organisation-asset'},
  {key: 'platformAsset', collection: 'platform-asset'}
];

const getDefaultState = (preload) => {
  return {
    tab: preload?.tab || 'results',
    filters: {
      year: {
        from: preload?.filters?.year?.from || new Date().getFullYear() - 3,
        to: preload?.filters?.year?.to || new Date().getFullYear()
      },
      organisationAsset: preload?.filters?.organisationAsset || null,
      platformAsset: preload?.filters?.platformAsset || null
    }
  }
}

const state = reactive(getDefaultState());
const pageReady = ref(false);
const routeState = getStateFromRoute();
const pdfUrl = ref(getReportPDFExportUrl());
const params = ref(null);

const stateDidChange = (doUpdateParams = false) => {
  const collectionFilters = dehydrateCollections(filterCollectionMap, state.filters);

  appendStateToRoute({
    ...state,
    filters: {
      ...state.filters,
      ...collectionFilters
    }
  });

  setTimeout(() => {
    pdfUrl.value = getReportPDFExportUrl();
  }, 100);

  if (doUpdateParams) {
    updateParams();
  }
}

const updateParams = debounce(() => {
  params.value = calcParams();
}, 250);

const calcParams = () => {
  if (state.filters.year.from.toString().length !== 4) {
    return null;
  }

  if (state.filters.year.to.toString().length !== 4) {
    return null;
  }

  if (!state.filters.organisationAsset && !state.filters.platformAsset) {
    return null;
  }

  const args = {
    year: state.filters.year,
    organisationAsset: [state.filters.organisationAsset?.id] || null,
    platformAsset: [state.filters.platformAsset?.id] || null
  }

  if (isOrganisationView()) {
    args.organisation = [getOrganisationId()];
  }

  return args;
}

const loadState = async () => {
  if (routeState?.filters) {
    const defaultState = getDefaultState(routeState);

    const collectionFilters = await rehydrateCollections(filterCollectionMap, defaultState.filters);

    defaultState.filters = {
      ...defaultState.filters,
      ...collectionFilters
    }

    Object.assign(state, defaultState);
  }

  pageReady.value = true;

  watch(organisationId, () => {
    stateDidChange(true);
  });

  watch(() => state.tab, () => {
    stateDidChange(false);
  });

  watch(() => state.filters.year.from, () => {
    stateDidChange(true);
  });

  watch(() => state.filters.year.to, () => {
    stateDidChange(true);
  });

  watch(() => state.filters.organisationAsset, () => {
    stateDidChange(true);
  });

  watch(() => state.filters.platformAsset, () => {
    stateDidChange(true);
  });

  stateDidChange(true);
}

onActivated(() => {
  if (pageReady.value) {
    stateDidChange(false);
  } else {
    loadState();
  }
});

</script>
